"use client"

import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { AlertCircle, RefreshCw, X } from '@/lib/icons'
import { getVersionInfo } from '@/lib/version'
import { useTranslations } from 'next-intl'

interface VersionInfo {
  version: string
  buildTime: string
  buildId: string
}

interface UpdateNotificationProps {
  onReload: () => void
  onDismiss: () => void
}

function UpdateNotification({ onReload, onDismiss }: UpdateNotificationProps) {
  const t = useTranslations('notifications')
  
  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm">
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 shadow-lg">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-medium text-orange-800">
              {t('updateAvailable')}
            </h3>
            <p className="text-sm text-orange-700 mt-1">
              {t('updateAvailableDescription')}
            </p>
            <div className="flex gap-2 mt-3">
              <Button
                size="sm"
                onClick={onReload}
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                {t('reloadNow')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={onDismiss}
                className="border-orange-300 text-orange-700 hover:bg-orange-50"
              >
                {t('later')}
              </Button>
            </div>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={onDismiss}
            className="h-6 w-6 p-0 text-orange-600 hover:bg-orange-100"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}

export function VersionDisplay() {
  const [currentVersion, setCurrentVersion] = useState<VersionInfo | null>(null)
  const [latestVersion, setLatestVersion] = useState<string | null>(null)
  const [showUpdateNotification, setShowUpdateNotification] = useState(false)
  const [isCheckingUpdate, setIsCheckingUpdate] = useState(false)
  const [lastChecked, setLastChecked] = useState<Date | null>(null)

  // Get current version info
  useEffect(() => {
    const versionInfo = getVersionInfo()
    setCurrentVersion(versionInfo)
  }, [])

  // Check for updates
  const checkForUpdates = async () => {
    if (isCheckingUpdate) return
    
    setIsCheckingUpdate(true)
    try {
      const response = await fetch('/api/version', {
        method: 'GET',
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
        },
      })
      
      if (response.ok) {
        const data = await response.json()
        setLatestVersion(data.version)
        setLastChecked(new Date())
        
        // Check if update is needed
        if (currentVersion && data.version !== currentVersion.version) {
          setShowUpdateNotification(true)
        }
      }
    } catch (error) {
      console.warn('Failed to check for updates:', error)
    } finally {
      setIsCheckingUpdate(false)
    }
  }

  // Check for updates on mount and periodically
  useEffect(() => {
    if (currentVersion) {
      checkForUpdates()
      
      // Check every 5 minutes
      const interval = setInterval(checkForUpdates, 5 * 60 * 1000)
      
      return () => clearInterval(interval)
    }
  }, [currentVersion])

  // Handle reload
  const handleReload = () => {
    // Clear all caches and reload
    if (typeof window !== 'undefined') {
      if ('caches' in window) {
        caches.keys().then((cacheNames) => {
          cacheNames.forEach((cacheName) => {
            caches.delete(cacheName)
          })
        }).finally(() => {
          (window as unknown as { location: { reload: () => void } }).location.reload()
        })
      } else {
        (window as unknown as { location: { reload: () => void } }).location.reload()
      }
    }
  }

  // Handle dismiss notification
  const handleDismiss = () => {
    setShowUpdateNotification(false)
    // Show again in 30 minutes if still outdated
    setTimeout(() => {
      if (latestVersion && currentVersion && latestVersion !== currentVersion.version) {
        setShowUpdateNotification(true)
      }
    }, 30 * 60 * 1000)
  }

  if (!currentVersion) return null

  const isOutdated = latestVersion && latestVersion !== currentVersion.version
  const formatTime = (date: Date) => date.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit' 
  })

  return (
    <>
      {/* Version display in bottom-right corner */}
      <div className="fixed bottom-4 right-4 z-40">
        <div className="flex flex-col items-end gap-2">
          {/* Version badge */}
          <Badge
            variant={isOutdated ? "destructive" : "secondary"}
            className="text-xs cursor-pointer hover:opacity-80 transition-opacity"
            onClick={checkForUpdates}
            title={`Click to check for updates${lastChecked ? `\nLast checked: ${formatTime(lastChecked)}` : ''}`}
          >
            {isCheckingUpdate ? (
              <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
            ) : isOutdated ? (
              <AlertCircle className="h-3 w-3 mr-1" />
            ) : null}
            v{currentVersion.version}
            {isOutdated && latestVersion && (
              <span className="ml-1 opacity-75">→ v{latestVersion}</span>
            )}
          </Badge>
          
          {/* Build info (only in development) */}
          {process.env.NODE_ENV === 'development' && (
            <div className="text-xs text-muted-foreground bg-background/80 px-2 py-1 rounded border">
              Build: {currentVersion.buildId}
            </div>
          )}
        </div>
      </div>

      {/* Update notification popup */}
      {showUpdateNotification && (
        <UpdateNotification
          onReload={handleReload}
          onDismiss={handleDismiss}
        />
      )}
    </>
  )
}
